import apiClient from 'commons/api/client/apiClient';
import { PUBLISHING_JOURNEY } from 'commons/publishing/publishingJourney';
import { PUBLISHING_SCOPE } from 'commons/publishing/publishingScope';

let resolveOnDataLoaded;

// Shared so concurrent router guards join one request instead of stacking.
let fetchPromise = null;

export default {
  namespaced: true,
  state: {
    data: {},
    onDataLoaded: new Promise((resolve) => {
      resolveOnDataLoaded = resolve;
    }),
    loaded: false,
    loadError: false,
  },
  getters: {
    getSetting: (state) => (key) =>
      state.data[key] ? state.data[key].value : undefined,
    getListSetting: (state) => (key) =>
      state.data[key] ? state.data[key].value : [],
    mpEnabled: (state, getters) => getters.getSetting('MP_ENABLED'),
    digitalProductsEnabled: (state, getters) =>
      getters.getSetting('DIGITAL_PRODUCTS'),
    publishingCoreEnabled: (state, getters) =>
      getters.getPublishingJourneyAvailabilityForScope(
        null,
        PUBLISHING_JOURNEY.PUBC,
      ),
    lastNpsSubmit: (state, getters) => getters.getSetting('LAST_PA_NPS_SUBMIT'),
    aiEnabled: (state, getters) =>
      ['ENABLED', 'INTERNAL'].includes(getters.getSetting('AI_ENABLED')),
    getPublishingJourneyAvailabilityForScope:
      (state, getters) => (scope, publishingJourney) => {
        if (scope === PUBLISHING_SCOPE.SHOP) {
          return getters.getPublishingJourneyAvailabilityShop(
            publishingJourney,
          );
        } else if (scope === PUBLISHING_SCOPE.MP) {
          return getters.getPublishingJourneyAvailabilityMp(publishingJourney);
        } else {
          return (
            getters.getPublishingJourneyAvailabilityShop(publishingJourney) ||
            getters.getPublishingJourneyAvailabilityMp(publishingJourney)
          );
        }
      },
    getPublishingJourneyAvailabilityShop:
      (state, getters, rootState, rootGetters) => (publishingJourney) =>
        ['SHOP', 'BOTH'].includes(rootGetters['user/partnerType']) &&
        getters
          .getListSetting('PUBLISHING_JOURNEY_SHOP')
          .includes(publishingJourney.key),
    getPublishingJourneyAvailabilityMp:
      (state, getters, rootState, rootGetters) => (publishingJourney) =>
        ['MP', 'BOTH'].includes(rootGetters['user/partnerType']) &&
        getters
          .getListSetting('PUBLISHING_JOURNEY_MP')
          .includes(publishingJourney.key),
    newPublishingJourneyAvailabilityMp: (state, getters) =>
      getters.getPublishingJourneyAvailabilityForScope(
        PUBLISHING_SCOPE.MP,
        PUBLISHING_JOURNEY.PUBC,
      ),
    newPublishingJourneyAvailabilityShop: (state, getters) =>
      getters.getPublishingJourneyAvailabilityForScope(
        PUBLISHING_SCOPE.SHOP,
        PUBLISHING_JOURNEY.PUBC,
      ),
    oldPublishingJourneyAvailabilityMp: (state, getters) =>
      getters.getPublishingJourneyAvailabilityForScope(
        PUBLISHING_SCOPE.MP,
        PUBLISHING_JOURNEY.FLEXI,
      ),
    oldPublishingJourneyAvailabilityShop: (state, getters) =>
      getters.getPublishingJourneyAvailabilityForScope(
        PUBLISHING_SCOPE.SHOP,
        PUBLISHING_JOURNEY.FLEXI,
      ),
    oldPublishingJourneyAvailabilityBoth: (state, getters) =>
      getters.oldPublishingJourneyAvailabilityMp &&
      getters.oldPublishingJourneyAvailabilityShop,
  },
  mutations: {
    fetchSettings(state, data) {
      data.forEach((item) => {
        state.data[item.key] = item;
      });
      resolveOnDataLoaded();
      state.loaded = true;
      state.loadError = false;
    },
    setLoadError(state) {
      state.loadError = true;
      // Router guards, the dashboard and analytics all await onDataLoaded. If
      // it only ever resolved on success a settings outage froze the whole app
      // (DEV-231297), so it has to settle here too - callers can tell the
      // degraded state apart via `loaded` / `loadError`.
      resolveOnDataLoaded();
    },
    setSetting(state, { key, value }) {
      state.data[key].value = value;
    },
  },
  actions: {
    fetchSettings({ commit, rootState }) {
      if (!fetchPromise) {
        fetchPromise = (async () => {
          try {
            const { data } = await apiClient.request({
              method: 'GET',
              url: `/api/v1/partners/${rootState.user.data.id}/settings`,
            });
            commit('fetchSettings', data);
          } catch (error) {
            // Deliberately not rethrown: `loaded` / `loadError` carry the
            // outcome, and nothing awaiting settings should be aborted by them
            // being unavailable.
            console.error('settings/fetchSettings failed', error);
            commit('setLoadError');
          }
        })().finally(() => {
          fetchPromise = null;
        });
      }

      return fetchPromise;
    },
    /**
     * Wait for settings, and heal a load that previously failed.
     *
     * Without this a single failed fetch left the session running on an empty
     * settings object for good - `onDataLoaded` is already resolved, so every
     * later awaiter sails straight past it and `loaded` never becomes true.
     * Router guards go through here so the session recovers once the service
     * does (DEV-231297). It never rejects; read `loaded` / `loadError`.
     */
    ensureLoaded({ state, dispatch }) {
      if (state.loaded) {
        return Promise.resolve();
      }

      if (state.loadError) {
        // Deliberately not awaited. Blocking a navigation on a service already
        // known to be down would reintroduce exactly the hang this is meant to
        // prevent - with the request timeout and retries, up to a minute of it.
        // This navigation runs on what we have; the next one gets the settings.
        dispatch('fetchSettings');
        return Promise.resolve();
      }

      // Initial fetch still in flight, dispatched by the login init.
      return state.onDataLoaded;
    },
    async setSetting({ commit, rootState, state }, { key, value }) {
      if (!Object.keys(state.data).includes(key)) {
        return Promise.reject();
      }

      await commit('setSetting', { key, value });
      await apiClient.request({
        method: 'PUT',
        url: `/api/v1/partners/${rootState.user.data.id}/settings/${key}`,
        data: state.data[key],
      });
    },
  },
};
